<template>
    <b-sidebar
        id="sidebar-import-invoices"
        sidebar-class="sidebar-lg"
        bg-variant="white"
        v-model="isImportSidebarVisible"
        shadow
        backdrop
        no-header
        right
    >
        <template #default="{ hide }">
            <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
                <h5 class="mb-0">{{ i18nT(`Import multiple invoices`) }}</h5>
                <feather-icon
                    class="ml-1 cursor-pointer"
                    icon="XIcon"
                    size="16"
                    @click="hide"
                />
            </div>

            <div class="p-2">
                <!-- Dropzone Area -->
                <div
                    id="files-dropzone"
                    class="dropzone mb-3 p-3 border-dashed text-center"
                    @dragover.prevent
                    @dragenter.prevent
                    @drop.prevent="handleDrop"
                >
                    <p>{{i18nT(`Drag and drop your invoice files here`)}}</p>
                </div>

                <!-- Files List -->
                <div v-if="invoicesForProcessing.length">
                    <div
                        v-for="(invoice, index) in invoicesForProcessing"
                        :key="index"
                        class="invoice-item mb-2 border rounded p-2"
                    >
                        <div class="d-flex align-items-center justify-content-between">
                            <div class="d-flex align-items-center">
                                <!-- Icon: spinner when processing, checkmark when success, X when error -->
                                <span v-if="invoice.status === 'processing'" class="spinner-border spinner-border-sm mr-2"></span>
                                <feather-icon
                                    v-if="invoice.status === 'success'"
                                    icon="CheckIcon"
                                    size="16"
                                    class="mr-2 text-success"
                                />
                                <feather-icon
                                    v-if="invoice.status === 'error'"
                                    icon="XIcon"
                                    size="16"
                                    class="mr-2 text-danger"
                                />
                                <span>{{ invoice.file.name }}</span>
                            </div>
                            <!-- Show progress percentage if processing -->
                            <small v-if="invoice.status === 'processing'">{{ invoice.progress }}%</small>
                        </div>
                        <!-- Progress Bar -->
                        <b-progress :value="invoice.progress" max="100" height="4px" class="mt-2">
                            <b-progress-bar
                                :value="invoice.progress"
                                :variant="invoice.status === 'success' ? 'success' : 'primary'"
                            />
                        </b-progress>
                    </div>

                    <!-- Clear button appears only if there were errors -->
                    <b-button
                        v-if="hasError"
                        variant="danger"
                        size="sm"
                        @click="clearQueue"
                        class="mt-2"
                    >
                        {{i18nT(`Clear Queue`)}}
                    </b-button>
                </div>
            </div>

        </template>
    </b-sidebar>
</template>

<script>
import {
    BSidebar,
    BButton,
    BProgress,
    BProgressBar,
} from "bootstrap-vue";


import { useUtils as useI18Utils } from "@core/libs/i18n";
const { i18n } = useI18Utils();
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
    components: {
        BSidebar,
        BButton,
        BProgress,
        BProgressBar,
    },
    data() {
        return {
            isImportSidebarVisible: false,
            currentInvoiceIndex: 0,
            // invoicesForProcessing will hold objects of the form:
            // { file: File, progress: 0, status: 'pending' | 'processing' | 'success' | 'error' }
            invoicesForProcessing: []
        };
    },
    computed: {
        hasError() {
            return this.invoicesForProcessing.some(
                (invoice) => invoice.status === "error"
            );
        }
    },
    methods: {
        resetInvoiceQueue() {
            this.currentInvoiceIndex = 0;
            this.invoicesForProcessing = [];
        },
        clearQueue() {
            this.resetInvoiceQueue();
            this.$toast({
                component: ToastificationContent,
                position: "top-right",
                props: {
                    title: i18n("Queue cleared"),
                    icon: "InfoIcon",
                    variant: "danger"
                }
            });
        },
        handleDrop(e) {
            // Get files from drop event
            const droppedFiles = Array.from(e.dataTransfer.files);
            // Create invoice objects and add them to the processing queue
            droppedFiles.forEach((file) => {
                this.invoicesForProcessing.push({
                    file,
                    progress: 0,
                    status: "pending"
                });
            });
            // If there are files and we are not already processing, start the first upload
            if (this.invoicesForProcessing.length && this.currentInvoiceIndex === 0) {
                this.nextInvoice();
            }
        },
        nextInvoice() {
            if (this.currentInvoiceIndex < this.invoicesForProcessing.length) {
                // Only process files that are still pending
                const currentInvoice = this.invoicesForProcessing[this.currentInvoiceIndex];
                if (currentInvoice.status === "pending") {
                    this.submitInvoice(currentInvoice);
                } else {
                    // Skip already processed files (in case you implement retries)
                    this.currentInvoiceIndex++;
                    this.nextInvoice();
                }
            } else {
                // All files have been attempted
                if (this.hasError) {
                    this.$toast({
                        component: ToastificationContent,
                        position: "top-right",
                        props: {
                            title: i18n("Processing complete. Some invoices failed."),
                            icon: "InfoIcon",
                            variant: "danger"
                        }
                    });
                } else {
                    this.$toast({
                        component: ToastificationContent,
                        position: "top-right",
                        props: {
                            title: i18n("All invoices have been processed successfully."),
                            icon: "InfoIcon",
                            variant: "success"
                        }
                    });
                    // Reset the queue automatically if no errors
                    this.resetInvoiceQueue();
                }
            }
        },
        submitInvoice(invoice) {
            // Mark as processing
            invoice.status = "processing";

            const formData = new FormData();
            formData.append("invoiceDocument", invoice.file);
            formData.append("draft", 1);
            formData.append("isClient", 0);

            // Replace this.$http with your HTTP client (e.g., Axios) and ensure it supports onUploadProgress
            this.$http
                .post("invoices/invoiceFile", formData, {
                    onUploadProgress: (progressEvent) => {
                        // Calculate progress percentage
                        invoice.progress = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                    }
                })
                .then((resp) => {
                    // When upload is complete, mark the invoice as successful
                    invoice.status = "success";
                    this.$emit('processed');
                    // Proceed to the next invoice
                    this.currentInvoiceIndex++;
                    this.nextInvoice();
                })
                .catch((err) => {
                    // Mark invoice as error if the submission fails
                    invoice.status = "error";
                    this.$toast({
                        component: ToastificationContent,
                        position: "top-right",
                        props: {
                            title: i18n("Error processing invoice"),
                            icon: "InfoIcon",
                            variant: "danger"
                        }
                    });
                    // Proceed to the next invoice despite the error
                    this.currentInvoiceIndex++;
                    this.nextInvoice();
                });
        }
    }
};
</script>

<style scoped>
/* Basic styling for the dropzone */
.dropzone {
    border: 2px dashed #ced4da;
    border-radius: 0.25rem;
    background-color: #f8f9fa;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.dropzone:hover {
    background-color: #e9ecef;
}

.invoice-item {
    background: #fff;
}
</style>
